import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { getTenantId } from "@/utils/tenant";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";

/**
 * 백엔드가 LocalDateTime(Z 없음)으로 반환하는 타임스탬프에 "Z"를 붙여
 * JavaScript에서 UTC로 올바르게 파싱되도록 정규화
 */
const ISO_DATETIME_WITHOUT_TZ = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

function normalizeTimestamps(data: unknown): unknown {
    if (typeof data === "string" && ISO_DATETIME_WITHOUT_TZ.test(data)) {
        return data + "Z";
    }
    if (Array.isArray(data)) {
        return data.map(normalizeTimestamps);
    }
    if (data && typeof data === "object") {
        const result: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(data)) {
            result[key] = normalizeTimestamps(value);
        }
        return result;
    }
    return data;
}

export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        "Pragma": "no-cache",
    },
    withCredentials: true,
});

// Request interceptor - 토큰 및 테넌트 헤더 추가
apiClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        // Access Token 추가
        // localStorage에서 직접 읽지 않고, Zustand store를 통해 접근하는 것이 더 안전하지만
        // interceptor에서는 store에 직접 접근하기 어려우므로 localStorage를 사용
        // 단, 토큰이 유효한 경우에만 추가
        try {
            const authStorage = localStorage.getItem("auth-storage");
            if (authStorage) {
                const parsed = JSON.parse(authStorage);
                // Zustand persist는 { state: {...} } 형태로 저장
                const state = parsed?.state || parsed;
                if (state?.accessToken) {
                    config.headers.Authorization = `Bearer ${state.accessToken}`;
                }
            }
        } catch (error) {
            // localStorage 파싱 실패 시 무시 (토큰 없이 요청)
            console.warn("Failed to parse auth storage:", error);
        }

        // Tenant ID 헤더 추가 (개발 환경)
        const tenantId = getTenantId();
        if (tenantId) {
            config.headers["X-Tenant-Id"] = tenantId;
        } else if (import.meta.env.DEV) {
            console.warn('[API Client] No tenant ID found - X-Tenant-Id header not set');
        }

        // 기기 토큰 헤더 (LC 기기 게이팅) — device.ts 순환 import 방지 위해 키 직접 읽기
        const deviceToken = localStorage.getItem("quebon-device-token");
        if (deviceToken) {
            config.headers["X-Device-Token"] = deviceToken;
        }

        // 요청 body/header에는 카드번호·식별번호·비밀번호·토큰이 포함될 수 있으므로
        // 원문을 콘솔에 남기지 않는다. 진단에는 method + URL만 사용한다.
        if (import.meta.env.DEV) {
            console.warn('[API Request]', config.method?.toUpperCase(), config.url);
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Token refresh state (중복 갱신 방지)
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
    refreshSubscribers.push(cb);
}

function onTokenRefreshed(token: string) {
    refreshSubscribers.forEach((cb) => cb(token));
    refreshSubscribers = [];
}

function onRefreshFailed() {
    refreshSubscribers = [];
}

// Response interceptor - 타임스탬프 정규화 + 에러 처리
apiClient.interceptors.response.use(
    (response) => {
        if (response.data && response.config.responseType !== 'blob' && response.config.responseType !== 'arraybuffer') {
            response.data = normalizeTimestamps(response.data);
        }
        return response;
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        const url = originalRequest?.url;

        // 403(Forbidden): 권한 없음 → 로그아웃하지 않고 에러만 전달
        // (비즈니스 로직의 ForbiddenException은 세션 무효가 아님)
        if (error.response?.status === 403) {
            if (url && url.includes("/b2b/tenant/")) {
                console.error("Critical: Access denied to tenant info. This should be a public API.");
                localStorage.removeItem("auth-storage");
            }
            return Promise.reject(error);
        }

        // 401(Unauthorized): 토큰 만료 → 갱신 시도
        if (error.response?.status === 401) {
            if (url && (url.includes("/auth/me") || url.includes("/auth/refresh"))) {
                return Promise.reject(error);
            }

            if (originalRequest._retry) {
                goToLogin();
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve) => {
                    subscribeTokenRefresh((newToken: string) => {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        resolve(apiClient(originalRequest));
                    });
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const authStorage = localStorage.getItem("auth-storage");
                const parsed = authStorage ? JSON.parse(authStorage) : null;
                const state = parsed?.state || parsed;
                const refreshToken = state?.refreshToken;

                if (!refreshToken) {
                    throw new Error("No refresh token");
                }

                const response = await apiClient.post("/auth/refresh", { refreshToken });
                const { accessToken: newAccessToken, refreshToken: newRefreshToken, user } = response.data.data;

                // auth-storage 업데이트
                const updatedState = { ...state, accessToken: newAccessToken, refreshToken: newRefreshToken, user };
                localStorage.setItem("auth-storage", JSON.stringify({ state: updatedState }));

                isRefreshing = false;
                onTokenRefreshed(newAccessToken);

                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return apiClient(originalRequest);
            } catch (refreshError) {
                isRefreshing = false;
                onRefreshFailed();
                goToLogin();
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

function goToLogin() {
    localStorage.removeItem("auth-storage");
    // 관리자(LMS) 영역에서의 세션 만료는 학습자 로그인이 아니라 관리자 로그인으로 보낸다.
    const path = window.location.pathname;
    const target = path.startsWith("/admin") ? "/admin/login" : "/login";
    if (path !== target) {
        window.location.href = target;
    }
}

// API Response 타입
export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
    error?: string;
}
